import { useState } from 'react';
import { Plus, Loader2, Building, Pencil, Lock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useRetailHouseAccounts,
  useRetailHouseAccount,
  useCreateRetailHouseAccount,
  useUpdateRetailHouseAccount,
  usePostRetailHouseAccountItem,
  useSettleRetailHouseAccount,
} from '@/hooks/api/useRetailHouseAccounts';
import {
  RetailHouseAccountItemDirection,
  UserRole,
  type RetailHouseAccount,
} from '@pms/shared';

const ADMIN_ROLES = new Set<UserRole>([UserRole.ADMIN, UserRole.SUPER_ADMIN]);
const OPS_ROLES = new Set<UserRole>([
  UserRole.OPERATIONS,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
]);

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function RetailHouseAccountsPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canManage = !!user && ADMIN_ROLES.has(user.role);
  const canPost = !!user && OPS_ROLES.has(user.role);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<RetailHouseAccount | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useRetailHouseAccounts({ propertyId }, !!propertyId);
  const accounts: RetailHouseAccount[] = listQuery.data ?? [];

  if (user && !canPost) {
    return (
      <div>
        <PageHeader title="House Accounts" description="POS charge accounts" />
        <EmptyState icon={Lock} title="No access" description="You don't have access to house accounts." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="House Accounts"
        description="POS-scoped charge accounts with running balances"
        actions={
          canManage ? (
            <Button onClick={() => { setEditing(null); setShowCreate(true); }} disabled={!propertyId}>
              <Plus size={16} aria-hidden="true" />
              New Account
            </Button>
          ) : undefined
        }
      />

      {!propertyId ? (
        <EmptyState icon={Building} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={Building}
          title="No house accounts"
          description="Create a house account to charge and settle POS sales."
          {...(canManage
            ? { actionLabel: 'New Account', onAction: () => { setEditing(null); setShowCreate(true); } }
            : {})}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Description</Th>
                  <Th>Balance</Th>
                  <Th>Created</Th>
                  {canManage && <Th>Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accounts.map((acc) => (
                  <tr
                    key={acc.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedId(acc.id)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{acc.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{acc.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{acc.description ?? '--'}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{formatMoney(acc.balance)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(acc.createdAt)}</td>
                    {canManage && (
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setEditing(acc); setShowCreate(true); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                          aria-label="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / edit slide-over */}
      <SlideOver
        isOpen={showCreate}
        onClose={() => { setShowCreate(false); setEditing(null); }}
        title={editing ? 'Edit House Account' : 'New House Account'}
      >
        {propertyId && (
          <AccountForm
            propertyId={propertyId}
            editing={editing}
            onClose={() => { setShowCreate(false); setEditing(null); }}
          />
        )}
      </SlideOver>

      {/* Detail slide-over */}
      <SlideOver
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title="House Account"
        width="max-w-xl"
      >
        {selectedId !== null && <AccountDetail id={selectedId} canPost={canPost} />}
      </SlideOver>
    </div>
  );
}

// ─── Create / edit form ──────────────────────────────────────────────────────

function AccountForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: RetailHouseAccount | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateRetailHouseAccount();
  const updateMutation = useUpdateRetailHouseAccount();
  const mutationError = createMutation.error ?? updateMutation.error;
  const isPending = createMutation.isPending || updateMutation.isPending;

  const isValid = name.trim().length > 0 && code.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            name: name.trim(),
            code: code.trim(),
            description: description.trim() || undefined,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          name: name.trim(),
          code: code.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rha-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="rha-name"
          type="text"
          required
          value={name}
          onChange={(e) => { setName(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label htmlFor="rha-code" className="block text-sm font-medium text-gray-700 mb-1">
          Code <span className="text-red-500">*</span>
        </label>
        <input
          id="rha-code"
          type="text"
          required
          value={code}
          onChange={(e) => { setCode(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="mt-1 text-xs text-gray-400">Unique per property.</p>
      </div>
      <div>
        <label htmlFor="rha-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          id="rha-desc"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          id="rha-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="rha-active" className="text-sm font-medium text-gray-700">Active</label>
      </div>

      {mutationError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={isPending} disabled={!isValid}>
          {editing ? 'Update' : 'Create Account'}
        </Button>
      </div>
    </form>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────

function AccountDetail({ id, canPost }: { id: string; canPost: boolean }) {
  const { data, isLoading } = useRetailHouseAccount(id);
  const [showPost, setShowPost] = useState(false);
  const [showSettle, setShowSettle] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  const { account, items } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-gray-900">{account.name}</h3>
        <p className="text-xs font-mono text-gray-400">{account.code}</p>
        {account.description && <p className="text-sm text-gray-500">{account.description}</p>}
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-gray-500">Balance</span>
          <span className="text-xl font-bold text-gray-900">{formatMoney(account.balance)}</span>
        </div>
      </div>

      {/* Actions */}
      {canPost && (
        <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
          <Button size="sm" onClick={() => { setShowPost(true); setShowSettle(false); }}>
            <Plus size={14} aria-hidden="true" />
            Post Line
          </Button>
          <Button size="sm" variant="secondary" onClick={() => { setShowSettle(true); setShowPost(false); }}>
            Settle
          </Button>
        </div>
      )}

      {showPost && (
        <PostLineForm accountId={id} onClose={() => setShowPost(false)} />
      )}
      {showSettle && (
        <SettleForm accountId={id} balance={account.balance} accountName={account.name} onClose={() => setShowSettle(false)} />
      )}

      {/* Line items */}
      <div className="pt-2">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Transactions</h4>
        {items.length === 0 ? (
          <p className="text-sm text-gray-400">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <ThSm>Date</ThSm>
                  <ThSm>Type</ThSm>
                  <ThSm>Description</ThSm>
                  <ThSm>Amount</ThSm>
                  <ThSm>Posted by</ThSm>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => {
                  const isSettle = it.direction === RetailHouseAccountItemDirection.SETTLE;
                  return (
                    <tr key={it.id}>
                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{fmtDate(it.entryDate)}</td>
                      <td className="px-3 py-2 text-sm capitalize text-gray-700">{it.direction}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{it.note ?? '--'}</td>
                      <td className={cn('px-3 py-2 text-sm font-medium', isSettle ? 'text-green-700' : 'text-gray-600')}>
                        {isSettle ? '-' : '+'}{formatMoney(it.amount)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">{it.postedBy ?? '--'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Post line form ──────────────────────────────────────────────────────────

function PostLineForm({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [entryDate, setEntryDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const postMutation = usePostRetailHouseAccountItem();
  const parsedAmount = parseFloat(amount);
  const isValid =
    Number.isFinite(parsedAmount) && parsedAmount >= 0.01 && description.trim().length > 0;

  function clearError() {
    if (postMutation.isError) postMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    postMutation.mutate(
      {
        id: accountId,
        payload: {
          direction: RetailHouseAccountItemDirection.CHARGE,
          amount: parsedAmount,
          note: description.trim(),
          entryDate,
        },
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
      <p className="text-sm font-semibold text-gray-700">Post a charge</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="rhl-amount" className="block text-sm font-medium text-gray-700 mb-1">
            Amount <span className="text-red-500">*</span>
          </label>
          <input
            id="rhl-amount"
            type="number"
            step="0.01"
            min="0.01"
            required
            value={amount}
            onChange={(e) => { setAmount(e.target.value); clearError(); }}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="rhl-date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            id="rhl-date"
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div>
        <label htmlFor="rhl-desc" className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <input
          id="rhl-desc"
          type="text"
          required
          value={description}
          onChange={(e) => { setDescription(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {postMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(postMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="submit" size="sm" isLoading={postMutation.isPending} disabled={!isValid}>Post</Button>
      </div>
    </form>
  );
}

// ─── Settle form ─────────────────────────────────────────────────────────────

function SettleForm({
  accountId,
  balance,
  accountName,
  onClose,
}: {
  accountId: string;
  balance: string;
  accountName: string;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState('');
  const settleMutation = useSettleRetailHouseAccount();

  const parsedAmount = parseFloat(amount);
  const balanceNum = parseFloat(balance);
  const exceedsBalance =
    Number.isFinite(parsedAmount) && Number.isFinite(balanceNum) && parsedAmount > balanceNum;
  const isValid = Number.isFinite(parsedAmount) && parsedAmount >= 0.01 && !exceedsBalance;

  function clearError() {
    if (settleMutation.isError) settleMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    if (!window.confirm(`Settle ${formatMoney(parsedAmount)} against ${accountName}?`)) return;
    settleMutation.mutate(
      { id: accountId, payload: { amount: parsedAmount } },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
      <p className="text-sm font-semibold text-gray-700">Settle (pay off)</p>
      <div>
        <label htmlFor="rhs-amount" className="block text-sm font-medium text-gray-700 mb-1">
          Amount <span className="text-red-500">*</span>
        </label>
        <input
          id="rhs-amount"
          type="number"
          step="0.01"
          min="0.01"
          required
          value={amount}
          onChange={(e) => { setAmount(e.target.value); clearError(); }}
          placeholder="0.00"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {exceedsBalance && (
          <p className="mt-1 text-xs text-red-600">Exceeds the {formatMoney(balance)} balance.</p>
        )}
      </div>
      {settleMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(settleMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="submit" size="sm" isLoading={settleMutation.isPending} disabled={!isValid}>Settle</Button>
      </div>
    </form>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}

function ThSm({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
